// Application Components
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const RegisteredNursingExaminationNurseLicensureCompactForm = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const dynamic_submit_route2 =
        settingsData?.['form']?.['dynamic_submit_route2']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submission) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                DEC_FLA: string().required(
                    'This field is required to be entered or selected.'
                ),
                OTHER_STATE: string().required(
                    'This field is required to be entered or selected.'
                ),
            })
            schema.valid = {
                DEC_FLA: false,
                DEC_FLAErrMsg: '',
                OTHER_STATE: false,
                OTHER_STATEErrMsg: '',
            }
            await schema.yup.validate(
                { ...submission },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Nurse Licensure Compact: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (
                valid &&
                formsData?.['get-started']?.['values']?.['GetStarTwo'] === 'Y'
            ) {
                console.info('Nurse Licensure Compact: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(dynamic_submit_route2 ? dynamic_submit_route2 : '/')
            } else if (valid) {
                console.info('Nurse Licensure Compact: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Nurse Licensure Compact: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Nurse Licensure CompactFailed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <p>
                    <strong>
                        Requirements that must be met to qualify for a
                        multistate license from Florida:
                    </strong>
                </p>

                <p>
                    <ol>
                        <li>Florida must be the Primary State of Residence*</li>
                        <li>Must have passed the NCLEX or the SBTPE</li>
                        <li>
                            The status of <b>all</b> nursing licenses (CNA, LPN,
                            RN, and APRN) must be clear and unencumbered in all
                            jurisdictions**
                        </li>
                        <li>
                            Must not have <b>any</b> misdemeanor conviction***
                            related to the practice of nursing,{' '}
                            <b>regardless of adjudication</b>
                        </li>
                        <li>
                            Must not have <b>any</b> felony conviction***,{' '}
                            <b>regardless of adjudication</b>
                        </li>
                        <li>
                            Must not be enrolled with the Intervention Project
                            for Nurses (IPN) or any other treatment program for
                            impaired practitioners
                        </li>
                        <li>Must have a U.S. Social Security number</li>
                        <li>
                            Must have completed an LPN or RN program, PNEQ{' '}
                            <u>does not</u> qualify
                        </li>
                        <p>
                            <strong>
                                Applicants Educated Outside the U.S. or NCSBN
                                Jurisdictions Only{' '}
                            </strong>
                        </p>
                        <li>
                            Education must be evaluated by an independent
                            credentials review agency
                        </li>
                        <li>
                            Education completed in a language other than English
                            will require an English competency examination
                        </li>
                    </ol>
                </p>

                <p>
                    <strong>Terminology:</strong>
                </p>

                <p>
                    <strong>*Primary state of residence</strong> is defined by
                    the Compact as the &quot;person&apos;s declared fixed
                    permanent and principal home for legal purposes;
                    domicile.&quot;
                </p>

                <p>
                    <strong>**Encumbrance</strong> means &quot; revocation or
                    suspension of, or any limitation on, the full and
                    unrestricted practice of nursing, imposed by a licensing
                    board.&quot;
                </p>

                <p>
                    <strong>***Conviction</strong> is defined by being
                    &quot;convicted or found guilty, or has entered into an
                    agreed disposition other than a disposition that results in
                    nolle prosequi, for an offense under applicable state or
                    federal criminal law.&quot;
                </p>

                <p>
                    <strong>
                        Proof of primary residence may include but is not
                        limited to:
                    </strong>
                    <ol>
                        <li>Driver license with a home address</li>
                        <li>
                            Voter registration card displaying a home address
                        </li>
                        <li>
                            Federal income tax return declaring the primary
                            state of residence
                        </li>
                        <li>
                            W2 from U.S. Government or any bureau, division, or
                            agency thereof indicating the declared state of
                            residence
                        </li>
                    </ol>
                </p>

                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.DEC_FLA
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.DEC_FLA
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.DEC_FLA
                        }
                        legendText="Do you declare Florida to be your primary state of residence for multistate licensure and are you providing a Florida address? If you only want a single state license, select &ldquo;No.&rdquo;"
                        name="DEC_FLA"
                        invalid={formsData?.[digital_name]?.errors?.DEC_FLA}
                        invalidText={
                            formsData?.[digital_name]?.errors?.DEC_FLAErrMsg
                        }
                    >
                        <RadioButton
                            id="DEC_FLAYes"
                            name="DEC_FLAYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="DEC_FLANo"
                            name="DEC_FLANo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <p>
                        If you{' '}
                        <strong>
                            do not have a current Florida mailing address, and
                            wish to have a multistate license, you must provide
                        </strong>{' '}
                        one of the documents listed above. If Florida is not
                        your primary state of residence, you are{' '}
                        <strong>not eligible</strong> for a Florida multistate
                        license and your application will be processed for a
                        single state license.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.OTHER_STATE
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.OTHER_STATE
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.OTHER_STATE
                        }
                        legendText="Do you hold an active NLC multistate license in another state?"
                        name="OTHER_STATE"
                        invalid={formsData?.[digital_name]?.errors?.OTHER_STATE}
                        invalidText={
                            formsData?.[digital_name]?.errors?.OTHER_STATEErrMsg
                        }
                    >
                        <RadioButton
                            id="OTHER_STATEYes"
                            name="OTHER_STATEYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="OTHER_STATENo"
                            name="OTHER_STATENo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <p>
                        <strong>
                            A nurse may only hold one multistate license.
                        </strong>{' '}
                        If your declared primary state of residence is another
                        Compact state and you are not changing your primary
                        residence to Florida, you are{' '}
                        <strong>not eligible</strong> for a multistate license
                        in Florida and should{' '}
                        <strong>not submit your application,</strong> as your
                        NLC license allows you to practice in Florida.
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default RegisteredNursingExaminationNurseLicensureCompactForm
